import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import FileAttachments from "@/components/FileAttachments";

/**
 * Shared dialog for creating/editing a training record.
 *
 * Hits the generic `/api/training` CRUD (via modules.py) so existing
 * CSV import + PDF export contract is preserved. When a `course_id` is
 * chosen we also stamp `course_id` + `category` on the record so future
 * reads resolve without fuzzy matching.
 */
export default function TrainingRecordDialog({ open, onOpenChange, record, onSaved }) {
  const [form, setForm] = useState(defaults());
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...defaults(), ...(record || {}) });
    api.get("/training/courses", { params: { active_only: true } }).then((r) => setCourses(r.data.items || [])).catch(() => {});
    api.get("/auth/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {});
  }, [open, record]);

  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const chooseCourse = (id) => {
    const c = courses.find((x) => x.id === id);
    patch("course_id", id || null);
    if (c) {
      patch("course", c.name);
      patch("category", c.category);
      // Auto-populate expiry when both a completed_date and renewal are known.
      if (c.renewal_months && form.completed_date) {
        const d = new Date(form.completed_date);
        d.setMonth(d.getMonth() + c.renewal_months);
        patch("expiry_date", d.toISOString().slice(0, 10));
      }
    }
  };

  const chooseStaff = (id) => {
    const u = users.find((x) => x.id === id);
    patch("staff_user_id", id || null);
    if (u) patch("staff_name", u.name || u.email);
  };

  const save = async () => {
    if (!form.staff_name?.trim()) return toast.error("Staff member is required");
    if (!form.course?.trim()) return toast.error("Course is required");
    if (!form.completed_date) return toast.error("Completed date is required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (record?.id) await api.put(`/training/${record.id}`, payload);
      else await api.post("/training", payload);
      toast.success(record?.id ? "Record updated" : "Record added");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="training-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{record?.id ? "Edit Training Record" : "New Training Record"}</DialogTitle>
          <DialogDescription>Records feed the compliance dashboard and matrix in real time.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <FLabel label="Staff member *">
              <select
                value={form.staff_user_id || ""} onChange={(e) => chooseStaff(e.target.value)}
                data-testid="training-input-staff"
                className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2"
              >
                <option value="">— Choose staff member —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
              {!form.staff_user_id && (
                <Input value={form.staff_name || ""} onChange={(e) => patch("staff_name", e.target.value)} placeholder="Or type a name (for legacy imports)" className="mt-1" data-testid="training-input-staff-name" />
              )}
            </FLabel>
          </div>
          <div className="col-span-2">
            <FLabel label="Course *">
              <select
                value={form.course_id || ""} onChange={(e) => chooseCourse(e.target.value)}
                data-testid="training-input-course"
                className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2"
              >
                <option value="">— Choose from catalogue —</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.requirement_type})</option>)}
              </select>
              {!form.course_id && (
                <Input value={form.course || ""} onChange={(e) => patch("course", e.target.value)} placeholder="Or type a course name" className="mt-1" data-testid="training-input-course-name" />
              )}
            </FLabel>
          </div>
          <FLabel label="Provider"><Input value={form.provider || ""} onChange={(e) => patch("provider", e.target.value)} data-testid="training-input-provider" /></FLabel>
          <FLabel label="Certificate ID"><Input value={form.certificate_id || ""} onChange={(e) => patch("certificate_id", e.target.value)} data-testid="training-input-cert" /></FLabel>
          <FLabel label="Completed *"><Input type="date" value={form.completed_date || ""} onChange={(e) => patch("completed_date", e.target.value)} data-testid="training-input-completed" /></FLabel>
          <FLabel label="Expiry"><Input type="date" value={form.expiry_date || ""} onChange={(e) => patch("expiry_date", e.target.value)} data-testid="training-input-expiry" /></FLabel>
          <div className="col-span-2">
            <FLabel label="Notes"><Textarea rows={2} value={form.notes || ""} onChange={(e) => patch("notes", e.target.value)} data-testid="training-input-notes" /></FLabel>
          </div>
          {record?.id && (
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Certificate &amp; evidence</div>
              <p className="text-[11px] text-slate-500 mb-2">Attach the certificate PDF or a photo of the completion evidence. Everyone with access to this training record can view it.</p>
              <FileAttachments module="training" recordId={record.id} />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-training-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-training-save">{saving ? "Saving…" : (record?.id ? "Save changes" : "Add record")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function defaults() {
  return { staff_name: "", staff_user_id: null, course: "", course_id: null,
           category: "", provider: "", completed_date: "", expiry_date: "",
           certificate_id: "", notes: "" };
}

function FLabel({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}
